/**
 * Promise timeout utilities
 *
 * Provides timeout wrappers for any Promise, complementing
 * fetchWithTimeout.ts which is specific to fetch() calls.
 *
 * @example
 * ```typescript
 * import { withTimeout, TimeoutError } from '@/services/utils/promiseTimeout';
 *
 * // Wrap any promise with a timeout
 * try {
 *   const result = await withTimeout(
 *     someAsyncOperation(),
 *     10000,
 *     'Operation timed out'
 *   );
 * } catch (error) {
 *   if (isTimeoutError(error)) {
 *     console.log(`Timed out after ${error.timeout}ms`);
 *   }
 * }
 * ```
 */

/**
 * Custom error class for timeout failures
 */
export class TimeoutError extends Error {
  readonly timeout: number;

  constructor(message: string, timeout: number) {
    super(message);
    this.name = 'TimeoutError';
    this.timeout = timeout;
  }
}

/**
 * Check if an error is a TimeoutError
 */
export function isTimeoutError(error: unknown): error is TimeoutError {
  return error instanceof TimeoutError;
}

/**
 * Wrap a promise with a timeout.
 *
 * @param promise - The promise to wrap
 * @param ms - Timeout in milliseconds
 * @param errorMessage - Custom error message (default: 'Operation timed out')
 * @returns Promise that rejects with TimeoutError if timeout exceeded
 *
 * @example
 * ```typescript
 * const result = await withTimeout(
 *   navigator.serviceWorker.ready,
 *   10000,
 *   'Service worker ready timeout'
 * );
 * ```
 */
export async function withTimeout<T>(
  promise: Promise<T>,
  ms: number,
  errorMessage = 'Operation timed out'
): Promise<T> {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;

  const timeoutPromise = new Promise<never>((_, reject) => {
    timeoutId = setTimeout(() => {
      reject(new TimeoutError(errorMessage, ms));
    }, ms);
  });

  try {
    return await Promise.race([promise, timeoutPromise]);
  } finally {
    if (timeoutId !== undefined) {
      clearTimeout(timeoutId);
    }
  }
}

/**
 * Create a promise that resolves after a delay.
 * Useful for retry delays and testing.
 *
 * @param ms - Delay in milliseconds
 */
export function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
